import React from "react";
import {
  WSChip,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSInfiniteScroll,
  WSPage,
  WSText
} from "@wingspanhq/fe-component-library";
import { queriesPayerRows } from "@query";
import { SearchPanel } from "../../Invoicing/components/SearchPanel";
import { useHistory } from "react-router-dom";
import { PAYERS_PATH } from "../paths";
import { useUrlQueryFilters } from "../../../utils/router";
import { TablePayers } from "../components/TablePayers/TablePayers";
import { useBrowserPageTitle } from "../../../shared/hooks/useBrowserPageTitle";

import styles from "./styles.module.scss";

export const RouteSearchClients: React.FC = () => {
  useBrowserPageTitle("Search clients");

  const history = useHistory();
  const { filters, setFilters } = useUrlQueryFilters<{
    search: string;
  }>({
    search: ""
  });

  // v3 free-text search over the denormalized client rows (searchPayerRows), with
  // cursor pagination. The searchString filter requires >= 2 chars, so the query is
  // gated until then.
  const search = filters.search || "";
  const query = queriesPayerRows.searchPayerRows.usePaginatedQuery(
    { filter: { searchString: { eq: search } } },
    { enabled: search.length >= 2 }
  );
  const payers = query.flatData ?? [];
  const totalCount =
    query.data?.pages?.[0]?.pagination?.totalSize ?? payers.length;

  return (
    <WSPage>
      <SearchPanel
        mb="L"
        context="clients"
        onBack={() => {
          history.push(PAYERS_PATH);
        }}
        value={filters.search || ""}
        onSearch={newValue => {
          setFilters({
            ...filters,
            search: newValue
          });
        }}
        onClear={() => {
          setFilters({
            search: ""
          });
        }}
        placeholder="Search clients"
      />
      {!!filters.search && (
        <>
          <WSFlexBox.CenterY mb="L" className={styles.statsContainer}>
            <WSElement className={styles.resultsStats}>
              <WSText.ParagraphXs color="gray500">Showing:</WSText.ParagraphXs>
              <WSChip
                pillText={`${totalCount} item${totalCount === 1 ? "" : "s"}`}
                theme="neutral"
                pillType="noBackground"
              />
            </WSElement>
          </WSFlexBox.CenterY>
          {/* v1 wrapped this in WSQueries; a search in flight shows the table
              skeleton, and a failed search must not read as "no results".
              Loading is checked first — after the empty check there is a window
              where neither branch renders and the area goes blank. */}
          {query.isLoading ? (
            <TablePayers payers={[]} loading />
          ) : query.isError ? (
            <WSEmptyState
              hasBorder
              bgColor="gray"
              type="error"
              title="Couldn’t load clients"
              description="Something went wrong while searching clients."
              actions={{
                buttons: [
                  {
                    label: "Try again",
                    kind: "Secondary",
                    onClick: () => query.refetch()
                  }
                ]
              }}
            />
          ) : payers.length === 0 ? (
            <WSEmptyState
              hasBorder
              bgColor="gray"
              type="search"
              title={`No results for "${filters.search}"`}
              description="Try adjusting your search or clear it to see all items."
              actions={{
                buttons: [
                  {
                    label: "Clear search",
                    kind: "Secondary",
                    onClick: () => {
                      setFilters({
                        search: ""
                      });
                    }
                  }
                ]
              }}
            />
          ) : (
            <WSInfiniteScroll
              onLoad={() => query.fetchNextPage()}
              loadMore={payers.length > 0}
              endOfList={!query.hasNextPage}
              loading={!!query.isFetchingNextPage}
            >
              <TablePayers payers={payers} />
            </WSInfiniteScroll>
          )}
        </>
      )}
    </WSPage>
  );
};
